import { useCallback, useEffect, useMemo, useState } from "react";
import { shallow } from "zustand/shallow";

import { getDataFromPart } from "@hsl/core/fund-page/services";
import useRunOnVisible from "@hsl/core/hooks/useRunOnVisible";
import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import { type ShareClassDataWithType } from "@hsl/fund-explorer/metaSchemas";
import { messageSchema } from "@hsl/fund-explorer/schemas";

import useIsOpen from "../../hooks/useIsOpen";
import useShowFundCard from "../../hooks/useShowFundCard";
import { generateLocalId } from "../../store/helpers";
import useExplorerStore from "../../store/store";
import Collapsible from "../Collapsible";
import { InfoMessage } from "../InfoMessage";
import { FundCardOverlay } from "../Subscription/FundCardOverlay/FundCardOverlay";
import FundCardHead from "./components/FundCardHead";
import { FundCardModal } from "./components/FundCardModal";
import { FundCardResponsiveContent } from "./components/FundCardResponsiveContent";
import FundCardTabs from "./components/FundCardTabs";
import FundSummary from "./components/FundSummary";

export type Props = {
    fund: string;
    className?: string;
};

const FundCard = (props: Props) => {
    const { audienceId, routeId, infoPartId } = useExplorerStore(
        (state) => state.config,
    );
    const { getFundById, getShareClassById, cardMode, languageId } =
        useExplorerStore(
            ({
                getFundById,
                getShareClassById,
                cardMode,
                config: { languageId },
            }) => ({
                getFundById,
                getShareClassById,
                cardMode,
                languageId,
            }),
            shallow,
        );
    const scId = generateLocalId("shareclass");
    const { expandCollapseEvent } = useYardActionEvents();
    const [messages, setMessages] = useState<
        { severity: "warning" | "info"; data: string }[]
    >([]);
    const [showModal, setShowModal] = useState(false);
    const fund = getFundById(props.fund);
    const showFundCard = useShowFundCard(props.fund);
    const isOpen = useIsOpen(props.fund);
    const [openKey, setOpenKey] = useState(Date.now());

    const { buttonClickEvent } = useYardActionEvents();
    const [subscriptionOverlay, setSubscriptionOverlay] = useState<
        "none" | "watch" | "subscribe"
    >("none");
    const basket = useFCSubscriptionStore((state) => state.selectionBasket);
    const toggleBasketItem = useFCSubscriptionStore(
        (state) => state.toggleBasketItem,
    );
    const userSubscriptions = useFCSubscriptionStore(
        (state) => state.userSubscriptions,
    );
    const isUpdated = useFCSubscriptionStore(
        (state) => state.isUpdatedSubAsset,
    );

    const applicableSubscriptions = userSubscriptions.filter(
        (s) => s.fundId.split("-")[1] === String(fund?.data.id),
    );

    const basketHasItems = basket.funds.length > 0;
    const isWatching = applicableSubscriptions.some(
        (s) => s.subscriptionType === "WATCHLIST",
    );
    const isSubscribed = applicableSubscriptions.some(
        (s) =>
            s.subscriptionType === "FULL" &&
            s.subscriptionStatus === "CONFIRMED",
    );
    const hasUpdates = applicableSubscriptions.some(isUpdated);

    const itemInBasket = basket.funds.some((f) => f.data.id === fund?.data.id);

    const shareClasses = useMemo(() => {
        return fund?.data.share_classes
            .map((id) => getShareClassById(generateLocalId("shareclass")(id)))
            .filter((sc): sc is ShareClassDataWithType => Boolean(sc));
    }, [fund, getShareClassById]);

    const handleSubscriptionButtonClick = useCallback(
        (mode: "watch" | "subscribe") => (_cookied: boolean) => {
            buttonClickEvent({
                name: `Add to ${
                    mode === "watch" ? "watchlist" : "subscription"
                }`,
                fund: {
                    name: fund?.data.data.name ?? "",
                    type: fund?.type ?? "",
                    id: fund?.data.id ?? 0,
                },
                details: fund?.data.data.name,
            });
            if (basketHasItems) {
                toggleBasketItem(fund!, shareClasses);
            } else {
                setSubscriptionOverlay(mode);
            }
        },
        [
            basketHasItems,
            buttonClickEvent,
            fund,
            shareClasses,
            toggleBasketItem,
        ],
    );

    useEffect(() => {
        setOpenKey(() => Date.now());
    }, [isOpen]);

    const ref = useRunOnVisible(
        useCallback(async () => {
            const data = await getDataFromPart({
                fundID: props.fund.split("-").pop()!,
                partId: infoPartId,
                audienceId,
                routeId: routeId,
                languageId,
            });
            const parse = messageSchema.safeParse(data);
            if (parse.success) {
                const test = Array.from(Object.keys(parse.data))
                    .filter((x) => {
                        const data =
                            parse.data[x as keyof typeof parse.data]?.part.data;
                        return typeof data === "string" && data.length > 0;
                    })
                    .map((x) => ({
                        severity: x as "warning" | "info",
                        data: parse.data[x as keyof typeof parse.data]?.part
                            .data as string,
                    }));
                setMessages(test);
            } else {
                console.error(parse.error);
            }
        }, [props.fund, audienceId, routeId, infoPartId, languageId]),
    );

    const renderLabel = useCallback(
        (open: boolean) =>
            fund && (
                <FundCardHead
                    fund={fund}
                    onSubscribeButtonClick={handleSubscriptionButtonClick}
                    isWatching={isWatching}
                    isSubscribed={isSubscribed}
                    itemInBasket={itemInBasket}
                    FundCardIsOpen={open}
                    hasUpdates={hasUpdates}
                />
            ),
        [
            fund,
            handleSubscriptionButtonClick,
            isSubscribed,
            isWatching,
            itemInBasket,
            hasUpdates,
        ],
    );

    const handleClose = useCallback(() => setShowModal(false), []);

    const renderMessages = () => (
        <>
            {messages.map((message, i) => (
                <InfoMessage
                    key={message.severity}
                    className={i === 0 ? "mt-4" : "mt-2"}
                    severity={message.severity}
                    html={message.data}
                />
            ))}
        </>
    );

    const handleCollapsibleToggle = useCallback(
        (_?: any, open?: boolean) => {
            const _fund = fund
                ? {
                      name: fund.data.data.name,
                      id: fund.data.id,
                      type: fund.type,
                  }
                : undefined;
            expandCollapseEvent({
                value: open ? "open" : "close",
                fund: _fund,
            });
        },
        [fund, expandCollapseEvent],
    );

    if (!fund || !showFundCard) return null;

    return (
        <div
            ref={ref}
            className={cx(
                "relative my-2 min-h-[60px] overflow-hidden bg-white py-3 pl-2 pr-4 shadow",
                props.className,
            )}
        >
            <Collapsible
                label={renderLabel}
                labelClassNames="w-full"
                className="hidden sm:block"
                isOpen={isOpen}
                key={openKey}
                onClick={handleCollapsibleToggle}
                labelAs="div"
            >
                <div className="pl-6">
                    <FundSummary fund={fund} tabs />
                    {renderMessages()}
                    <div className="pt-4">
                        <FundCardTabs fund={fund} type="pill" />
                    </div>
                </div>
            </Collapsible>
            <div className="px-1 sm:hidden">
                <FundCardResponsiveContent
                    fund={fund}
                    mode={cardMode}
                    onShowModal={() => setShowModal(true)}
                    onSubscribeButtonClick={handleSubscriptionButtonClick}
                    isWatching={isWatching}
                    isSubscribed={isSubscribed}
                    hasUpdates={hasUpdates}
                />
            </div>
            <FundCardModal
                show={showModal}
                onClose={handleClose}
                fund={fund}
                renderMessages={renderMessages}
                onSubscribeButtonClick={handleSubscriptionButtonClick}
                isWatching={isWatching}
                isSubscribed={isSubscribed}
                itemInBasket={itemInBasket}
                hasUpdates={hasUpdates}
            />
            <FundCardOverlay
                show={subscriptionOverlay}
                onClose={() => setSubscriptionOverlay("none")}
                fund={fund}
                shareClasses={fund.data.share_classes
                    .map((id) => getShareClassById(scId(id)))
                    .filter((sc): sc is ShareClassDataWithType => Boolean(sc))}
                isSubscribed={isSubscribed}
            />
        </div>
    );
};

export default FundCard;
